import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  ButtonGroup,
  IconButton,
  Nav,
  Loader,
  Panel,
  Whisper,
  Tooltip,
  Message,
  useToaster,
  Pagination
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList, MdPerson, MdPersonOff, MdInfoOutline } from 'react-icons/md';
import { Edit, Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import EditDrawerView from './EditDrawerView';
import DetailsDrawerView from './DetailsDrawerView';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBalances } from '@/services/balance.service';
import { getAdminUsers } from '@/services/user.service';
import { getServicelines } from '@/services/serviceline.service';
import * as XLSX from 'xlsx';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(state => state.balance);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const serviceLines = useSelector(state => state.serviceLine.data);
  const loadingServiceLine = useSelector(state => state.serviceLine.loading);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toaster = useToaster();
  const [filters, setFilters] = useState({
    userId: '',
    department: '',
    balanceStatus: ''
  });

  useEffect(() => {
    dispatch(getAdminUsers());
    dispatch(getServicelines());
  }, [dispatch]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchBalances(activeTab, new Date().toISOString().split('T')[0])).finally(() => {
      setIsLoading(false);
    });
  }, [dispatch, activeTab]);
  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const applyFilters = dataToFilter => {
    let filtered = [...dataToFilter];

    if (searchKeyword.trim()) {
      const searchStr = searchKeyword.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const fullName = `${item.idUser?.firstname || ''} ${
          item.idUser?.lastname || ''
        }`.toLowerCase();
        const registrationNumber = item.idUser?.registrationNumber?.toLowerCase() || '';
        const department = item.idUser?.serviceLine?.serviceLine?.toLowerCase() || '';
        const leaveBalance = item.LeaveBalance?.toString() || '';

        return (
          fullName.includes(searchStr) ||
          registrationNumber.includes(searchStr) ||
          department.includes(searchStr) ||
          leaveBalance.includes(searchStr)
        );
      });
    }

    if (filters.userId) {
      filtered = filtered.filter(item => item.idUser?._id === filters.userId);
    }

    if (filters.department && filters.department !== '') {
      filtered = filtered.filter(
        item => item.idUser?.serviceLine?.serviceLine === filters.department
      );
    }

    if (filters.balanceStatus && filters.balanceStatus !== '') {
      if (filters.balanceStatus === 'WITH_BALANCE') {
        filtered = filtered.filter(item => 'LeaveBalance' in item);
      }
      if (filters.balanceStatus === 'WITHOUT_BALANCE') {
        filtered = filtered.filter(item => !('LeaveBalance' in item));
      }
    }

    return filtered;
  };

  const filteredData = applyFilters(data);
  const paginatedData = filteredData.filter((v, i) => {
    const start = limit * (page - 1);
    const end = start + limit;
    return i >= start && i < end;
  });

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      department: '',
      balanceStatus: ''
    });
    setPage(1);
  };

  const handleEdit = rowData => {
    setSelectedUser(rowData);
    setShowEditDrawer(true);
  };

  const handleDetails = rowData => {
    setSelectedUser(rowData);
    setShowDetailsDrawer(true);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const balancesToExport = filteredData.map(item => ({
      'Full Name': `${item.idUser?.firstname || ''} ${item.idUser?.lastname || ''}`,
      'Registration Number': item.idUser?.registrationNumber || '',
      Team: item.idUser?.serviceLine?.serviceLine || '',
      'Leave Balance': item.LeaveBalance || '0',
      Status: activeTab === 'active' ? 'Active' : 'Inactive'
    }));

    downloadExcel(balancesToExport, 'leave-balances');
  };
  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };
  const { t } = useTranslation();
  return (
    <>
      <Nav
        appearance="subtle"
        activeKey={activeTab}
        onSelect={setActiveTab}
        style={{
          marginBottom: '20px',
          borderBottom: '1px solid #e5e5e5'
        }}
      >
        <Nav.Item eventKey="active">
          <Icon as={MdPerson} style={{ fontSize: '18px', marginRight: '8px' }} />
          {t('Active Users')} {activeTab === 'active' && `(${data.filter(item => item.idUser).length})`}
        </Nav.Item>
        <Nav.Item eventKey="passif">
          <Icon as={MdPersonOff} style={{ fontSize: '18px', marginRight: '8px' }} />
          {t('Inactive Users')} {activeTab === 'passif' && `(${data.filter(item => item.idUser).length})`}
        </Nav.Item>
      </Nav>

      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t("Search")} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <Icon as={ExportIcon} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>

      <Panel bodyFill>
        {isLoading ? (
          <Loader center content="Loading..." />
        ) : (
          <>
            <Table
              height={Math.max(getHeight(window) - 200, 400)}
              data={paginatedData}
              loading={loading}
              rowHeight={80}
              wordWrap="break-word"
            >
              <Column flexGrow={4}>
                <HeaderCell>{t('Full Name')}</HeaderCell>
                <Cell>
                  {rowData =>
                    `${rowData.idUser?.firstname || ''} ${rowData.idUser?.lastname || ''}`
                  }
                </Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Registration Number')}</HeaderCell>
                <Cell>{rowData => rowData.idUser?.registrationNumber}</Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Team')}</HeaderCell>
                <Cell>{rowData => rowData.idUser?.serviceLine?.serviceLine}</Cell>
              </Column>

              <Column flexGrow={3}>
                <HeaderCell>{t('Leave Balance')}</HeaderCell>
                <Cell dataKey="LeaveBalance" />
              </Column>
              <Column flexGrow={3}>
                <HeaderCell>{t('Sick Balance')}</HeaderCell>
                <Cell dataKey="sickBalance" />
              </Column>

              <Column width={100} fixed="right">
                <HeaderCell>{t('Actions')}</HeaderCell>
                <Cell>
                  {rowData => (
                    <Stack spacing={6}>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{t('Edit Balance')}</Tooltip>}
                      >
                        <IconButton
                          size="xs"
                          icon={<Edit style={{ color: 'blue' }} />}
                          onClick={() => handleEdit(rowData)}
                          id={`edit-${rowData._id}`}
                        />
                      </Whisper>
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{t('View Details')}</Tooltip>}
                      >
                        <IconButton
                          size="xs"
                          icon={<Icon as={MdInfoOutline} />}
                          onClick={() => handleDetails(rowData)}
                          id={`details-${rowData._id}`}
                        />
                      </Whisper>
                    </Stack>
                  )}
                </Cell>
              </Column>
            </Table>
            <div style={{ padding: 20 }}>
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="xs"
                layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                total={filteredData.length}
                limitOptions={[10, 30, 50]}
                limit={limit}
                activePage={page}
                onChangePage={setPage}
                onChangeLimit={handleChangeLimit}
              />
            </div>
          </>
        )}
      </Panel>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        serviceLines={serviceLines}
        usersLoading={usersLoading}
        serviceLinesLoading={loadingServiceLine}
        toaster={toaster}
      />

      <EditDrawerView
        open={showEditDrawer}
        selectedUser={selectedUser}
        onClose={() => setShowEditDrawer(false)}
        currentTab={activeTab}
      />

      <DetailsDrawerView
        open={showDetailsDrawer}
        selectedUser={selectedUser}
        onClose={() => setShowDetailsDrawer(false)}
      />
    </>
  );
};

export default DataTable;
